import { getCollectionReport } from 'lib/api/reports';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const Collection: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [folio, setFolio] = useState('');
  const [data, setData] = useState([]);

  const filters: Filter[] = [
    {
      label: 'Fecha de inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre Cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut Cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre Deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut Deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
  ];

  const generateQuery = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (businessName) params.append('businessName', businessName);
    if (stakeholderName) params.append('stakeholderName', stakeholderName);
    if (businessIdentifier)
      params.append('businessIdentifier', businessIdentifier);
    if (stakeholderIdentifier)
      params.append('stakeholderIdentifier', stakeholderIdentifier);
    if (folio) params.append('folio', folio);
    return params.toString();
  };

  const fetchData = async () => {
    const query = generateQuery();
    const response = await getCollectionReport(dispatch, query);
    setData(response?.conciliations || []);
    return response;
  };

  const triggerSearch = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      errorHandler(
        dispatch,
        error,
        'Error al obtener el reporte de recaudaciones',
      );
    } finally {
      setLoading(false);
    }
  };

  const setUp = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá verás todas las recaudaciones recibidas de un documento."
        buttons={[
          {
            name: 'Buscar',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
                maxMonths={1}
                shortcuts={false}
              />
            ),
          },
        ]}
      />

      <EnhancedTable
        dataHeaders={[
          { key: 'typeId', label: 'ID Tipo' },
          { key: 'type', label: 'Tipo' },
          { key: 'docType', label: 'Tipo Doc.' },
          { key: 'folio', label: 'Folio Doc.' },
          { key: 'requestId', label: 'ID Solicitud' },
          { key: 'requestType', label: 'Tipo Solicitud' },
          {
            key: 'requestOperationDate',
            label: 'Fecha Solicitud Factoring',
            type: 'date',
          },
          { key: 'business', label: 'Cliente' },
          { key: 'businessIdentifier', label: 'Rut Cliente' },
          { key: 'stakeholder', label: 'Deudor' },
          { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
          { key: 'fundName', label: 'Fondo' },
          { key: 'fundIdentifier', label: 'Rut Fondo' },
          { key: 'paidBy', label: 'Pagado por' },
          { key: 'paymentDate', label: 'Fecha Vencimiento Doc.', type: 'date' },
          { key: 'documentAmount', label: 'Monto Doc.', type: 'money' },
          {
            key: 'documentAdvanceAmount',
            label: 'Anticipo Doc.',
            type: 'money',
          },
          { key: 'amount', label: 'Monto Recaudado', type: 'money' },
          {
            key: 'conciliationDocumentAmount',
            label: 'Conciliado Doc.',
            type: 'money',
          },
          {
            key: 'conciliationArrearAmount',
            label: 'Conciliado Mora',
            type: 'money',
          },
          { key: 'date', label: 'Fecha Recaudación', type: 'date' },
          { key: 'executiveName', label: 'Ejecutivo' },
        ]}
        data={data}
        rowsPerPageDefault={25}
        lastElement
        exportToFile="xlsx"
        finalSumRow
      />
    </div>
  );
};

export default Collection;
